import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Table, Label, Message, Button, Card, Grid } from 'semantic-ui-react';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { FieldItem, FieldLabel, FieldList, FieldValue } from '../styles/styles';
import { H2, PageSection } from 'common/styles/page';
import { HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { authenticatedFetch } from 'common/utils/authFetch';

interface SQSQueue {
  name: string;
  url: string;
  attributes: {
    ApproximateNumberOfMessages?: string;
    ApproximateNumberOfMessagesNotVisible?: string;
    ApproximateNumberOfMessagesDelayed?: string;
    CreatedTimestamp?: string;
    LastModifiedTimestamp?: string;
    VisibilityTimeout?: string;
    MessageRetentionPeriod?: string;
    DelaySeconds?: string;
    ReceiveMessageWaitTimeSeconds?: string;
  };
}

interface SQSSummary {
  total_queues: number;
  total_messages: number;
  total_messages_in_flight: number;
  total_messages_delayed: number;
  celery_queues_count: number;
  reply_queues_count: number;
  other_queues_count: number;
}

interface SQSData {
  summary: SQSSummary & {
    total_queues: number;
  };
  queues: {
    celery_queues: SQSQueue[];
    reply_queues: SQSQueue[];
    other_queues: SQSQueue[];
  };
  server_name: string;
  server_prefix: string;
  region: string;
  filtering: {
    applied: boolean;
    criteria: string;
  };
}

const SQSQueueMonitoring: React.FC = () => {
  const [sqsData, setSqsData] = useState<SQSData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; content: string } | null>(
    null
  );
  const [activeCategory, setActiveCategory] = useState<'celery' | 'reply' | 'other'>('celery');

  // Fetch SQS queue data
  useEffect(() => {
    const fetchSQSData = async () => {
      setLoading(true);
      try {
        // Use the configured API base URL instead of relative URL
        const apiBaseUrl = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;
        const response = await authenticatedFetch(`${apiBaseUrl}/api/admin/v1/system-health/sqs_queues/`, {
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          setSqsData(data);
        } else {
          setMessage({ type: 'error', content: 'Failed to fetch SQS queue data' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'Failed to fetch SQS queue data' });
      } finally {
        setLoading(false);
      }
    };

    fetchSQSData();
  }, []);

  const getQueueTypeColor = (queueName: string) => {
    if (
      queueName.endsWith('-reply-celery-pidbox') ||
      (queueName.includes('-reply-') && queueName.includes('celery'))
    ) {
      return 'orange';
    } else if (queueName.toLowerCase().includes('celery')) {
      return 'blue';
    } else {
      return 'grey';
    }
  };

  const getMessageCountColor = (count: number) => {
    if (count === 0) return 'green';
    if (count < 10) return 'yellow';
    if (count < 50) return 'orange';
    return 'red';
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Unknown';
    return new Date(parseInt(timestamp) * 1000).toLocaleString();
  };

  const getActiveQueues = () => {
    if (!sqsData) return [];

    switch (activeCategory) {
      case 'celery':
        return sqsData.queues.celery_queues;
      case 'reply':
        return sqsData.queues.reply_queues;
      case 'other':
        return sqsData.queues.other_queues;
      default:
        return [];
    }
  };

  const queueData = getActiveQueues().map(queue => ({
    key: queue.name,
    name: queue.name,
    messages: parseInt(queue.attributes.ApproximateNumberOfMessages || '0'),
    messagesInFlight: parseInt(queue.attributes.ApproximateNumberOfMessagesNotVisible || '0'),
    messagesDelayed: parseInt(queue.attributes.ApproximateNumberOfMessagesDelayed || '0'),
    created: formatTimestamp(queue.attributes.CreatedTimestamp || ''),
    lastModified: formatTimestamp(queue.attributes.LastModifiedTimestamp || ''),
    visibilityTimeout: queue.attributes.VisibilityTimeout || 'Unknown',
    messageRetention: queue.attributes.MessageRetentionPeriod || 'Unknown',
    delaySeconds: queue.attributes.DelaySeconds || '0',
    receiveWaitTime: queue.attributes.ReceiveMessageWaitTimeSeconds || '0',
  }));

  if (!sqsData) {
    return (
      <Container>
        {message && (
          <Message negative={message.type === 'error'} onDismiss={() => setMessage(null)}>
            {message.content}
          </Message>
        )}
        <LoadingSpinner text='Loading SQS Queue Data...' />
      </Container>
    );
  }

  return (
    <Container>
      {message && (
        <Message negative={message.type === 'error'} onDismiss={() => setMessage(null)}>
          {message.content}
        </Message>
      )}

      {/* Summary Statistics */}
      {/* Top Stats — 2 rows of 4 cards */}
      <div style={statsGrid4}>
        {/* Row 1 */}
        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Total Queues</H2>
            <div style={value('#333')}>
              {sqsData.summary.total_queues.toLocaleString()}
            </div>
            <div style={sub}>Filtered</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Queues in SQS</H2>
            <div style={value('#616161')}>
              {sqsData.summary.total_queues?.toLocaleString() || '0'}
            </div>
            <div style={sub}>Environment Total</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Total Messages</H2>
            <div style={value('#2e7d32')}>
              {sqsData.summary.total_messages.toLocaleString()}
            </div>
            <div style={sub}>Across Filtered Queues</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>In Flight</H2>
            <div style={value('#1976d2')}>
              {sqsData.summary.total_messages_in_flight.toLocaleString()}
            </div>
            <div style={sub}>Not Visible</div>
          </Card>
        </div>

        {/* Row 2 */}
        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Delayed</H2>
            <div style={value('#f57c00')}>
              {sqsData.summary.total_messages_delayed.toLocaleString()}
            </div>
            <div style={sub}>Delayed Delivery</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Celery Queues</H2>
            <div style={value('#0288d1')}>
              {sqsData.summary.celery_queues_count.toLocaleString()}
            </div>
            <div style={sub}>Worker Queues</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Reply Queues</H2>
            <div style={value('#ef6c00')}>
              {sqsData.summary.reply_queues_count.toLocaleString()}
            </div>
            <div style={sub}>Async Replies</div>
          </Card>
        </div>

        <div style={gridItem}>
          <Card fluid raised style={cardStyle}>
            <H2>Other Queues</H2>
            <div style={value('#757575')}>
              {sqsData.summary.other_queues_count.toLocaleString()}
            </div>
            <div style={sub}>Miscellaneous</div>
          </Card>
        </div>
      </div>

      {/* Environment Info */}
      <PageSection padbottom>
        <Card fluid raised style={{ padding: '20px' }}>
          <H2 style={{ marginBottom: '16px' }}>SQS Environment Information</H2>

          <Grid columns={2} stackable divided>
            <Grid.Row>
              <Grid.Column>
                <FieldList>
                  <FieldItem>
                    <FieldLabel>Server</FieldLabel>
                    <FieldValue>{sqsData.server_name}</FieldValue>
                  </FieldItem>
                  <FieldItem>
                    <FieldLabel>Server Prefix</FieldLabel>
                    <FieldValue>{sqsData.server_prefix}</FieldValue>
                  </FieldItem>
                  <FieldItem>
                    <FieldLabel>Region</FieldLabel>
                    <FieldValue>{sqsData.region}</FieldValue>
                  </FieldItem>
                </FieldList>
              </Grid.Column>

              <Grid.Column>
                <FieldList>
                  <FieldItem>
                    <FieldLabel>Filtering</FieldLabel>
                    <FieldValue>{sqsData.filtering.criteria}</FieldValue>
                  </FieldItem>
                  <FieldItem>
                    <FieldLabel>Total Queues in SQS</FieldLabel>
                    <FieldValue>{sqsData.summary.total_queues}</FieldValue>
                  </FieldItem>
                  <FieldItem>
                    <FieldLabel>Filtered Queues</FieldLabel>
                    <FieldValue>{sqsData.summary.total_queues}</FieldValue>
                  </FieldItem>
                </FieldList>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card>
      </PageSection>


      {/* Category Tabs */}
      <div style={{ marginBottom: '16px' }}>
        <Button.Group>
          <Button
            active={activeCategory === 'celery'}
            onClick={() => setActiveCategory('celery')}
            color="blue"
          >
            Celery Queues ({sqsData.summary.celery_queues_count})
          </Button>
          <Button
            active={activeCategory === 'reply'}
            onClick={() => setActiveCategory('reply')}
            color="orange"
          >
            Reply Queues ({sqsData.summary.reply_queues_count})
          </Button>
          <Button
            active={activeCategory === 'other'}
            onClick={() => setActiveCategory('other')}
            color="grey"
          >
            Other Queues ({sqsData.summary.other_queues_count})
          </Button>
        </Button.Group>
      </div>

      {/* Queue Table */}
      <Table>
        <TableHeader>
            <HeaderCell>Queue</HeaderCell>
            <HeaderCell>Messages</HeaderCell>
            <HeaderCell>In Flight</HeaderCell>
            <HeaderCell>Delayed</HeaderCell>
            <HeaderCell>Created</HeaderCell>
            <HeaderCell>Last Modified</HeaderCell>
            <HeaderCell>Visibility Timeout</HeaderCell>
            <HeaderCell>Message Retention</HeaderCell>
        </TableHeader>
        <TableBody>
          {queueData.map(row => (
            <TableRow key={row.key}>
              <TableCell>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{row.name}</div>
                  <Label color={getQueueTypeColor(row.name) as any} size="tiny">
                    {activeCategory === 'celery'
                      ? 'Celery'
                      : activeCategory === 'reply'
                        ? 'Reply'
                        : 'Other'}
                  </Label>
                </div>
              </TableCell>
              <TableCell>
                <Label color={getMessageCountColor(row.messages) as any}>{row.messages}</Label>
              </TableCell>
              <TableCell>
                <Label color={getMessageCountColor(row.messagesInFlight) as any}>
                  {row.messagesInFlight}
                </Label>
              </TableCell>
              <TableCell>
                <Label color={getMessageCountColor(row.messagesDelayed) as any}>
                  {row.messagesDelayed}
                </Label>
              </TableCell>
              <TableCell>{row.created}</TableCell>
              <TableCell>{row.lastModified}</TableCell>
              <TableCell>{row.visibilityTimeout}s</TableCell>
              <TableCell>{row.messageRetention}s</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {queueData.length === 0 && (
        <Message info>
          <Message.Header>No Queues Found</Message.Header>
          <p>
            No {activeCategory} queues found for server prefix '{sqsData.server_prefix}'.
          </p>
          <p>Only queues starting with '{sqsData.server_prefix}' are shown.</p>
        </Message>
      )}
    </Container>
  );
};


export default SQSQueueMonitoring;

const Container = styled.div`
  padding: 24px;
`;

const statsGrid4 = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '10px',
  alignItems: 'stretch',
  marginBottom: '24px',
} as const;

const gridItem = { minWidth: 0 } as const;

const cardStyle = {
  margin: 0,
  width: '100%',
  height: '100%',
  border: '1px solid #d4d4d5',
  borderRadius: '6px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
} as const;

const value = (color = '#333') =>
  ({ fontSize: '24px', fontWeight: 'bold', color } as const);

const sub = { fontSize: '12px', color: '#666' } as const;
